import axios from "axios";
import apiUrlConfig from "../../environmentSettings";

/**
 * Returns promise which resolves to tables in a particular organization
 * @param {Number} orgId
 */
export const getTables = organizationId => {
	return axios
		.get("/tables", {
			params: {
				organizationId
			}
		})
		.then(response => response.data)
		.then(tables =>
			tables.map(table => {
				return {
					_id: table._id,
					organizationId: table.organizationId,
					static: table.static,
					tableAlias: table.tableAlias.concat(
						`${table.tableId !== null ? `(${table.tableId})` : ""}`
					),
					tableName: table.tableName,
					tableId: table.tableId
				};
			})
		);
};

export const validateAccessToken = accessToken => {
	return axios
		.post(
			`${apiUrlConfig.apiConfig.p3fyUrl}/ops/checkIfUserActive`,
			{},
			{
				headers: { Authorization: accessToken }
			}
		)
		.then(function(response) {
			return response.data;
		})
		.catch(function(err) {
			console.log();
			return err;
		});
};

export const logoutUser = accessToken => {
	return axios
		.post(
			`${apiUrlConfig.apiConfig.p3fyUrl}/users/logout`,
			{},
			{
				headers: { Authorization: accessToken }
			}
		)
		.then(function(response) {
			return response.data;
		})
		.catch(function(err) {
			console.log();
			return err;
		});
};

export const getUserInfo = (accessToken, userId) => {
	return axios
		.get(`${apiUrlConfig.apiConfig.p3fyUrl}/users/findOne`, {
			headers: { Authorization: accessToken },
			params: {
				filter: {
					where: {
						id: userId
					},
					fields: ["id", "email"],
					include: {
						relation: "personProfile",
						scope: {
							fields: ["name", "photoUrl"]
						}
					}
				}
			}
		})
		.then(response => response.data)
		.catch(err => {
			throw err;
		});
};

export const getLicenses = accessToken => {
	return axios
		.post(`${apiUrlConfig.apiConfig.p3fyUrl}/users/getLicenses`, {
			headers: { Authorization: accessToken }
		})
		.then(response => response.data)
		.catch(err => {
			throw err;
		});
};

export const getCustomApps = accessToken => {
	return axios
		.post(`${apiUrlConfig.apiConfig.p3fyUrl}/users/getApps`, {
			headers: { Authorization: accessToken }
		})
		.then(response => response.data)
		.catch(err => {
			throw err;
		});
};

export const getTagElements = (organizationId, table, query) => {
	return axios
		.post("aggregate/table", {
			organizationId,
			table,
			query
		})
		.then(response => {
			return response.data;
		});
};

export const getMappings = (organizationId, tableName) => {
	return axios
		.get("/tables/mappings", {
			params: {
				organizationId,
				tableName
			}
		})
		.then(response => response.data)
		.then(tableData => {
			tableData.keywords = [...tableData.dimensions, ...tableData.metrics];
			delete tableData.dimensions;
			delete tableData.metrics;
			return tableData;
		});
};

export const executeQuery = query => {
	return axios
		.post("/aggregate/table", query)
		.then(response => response.data)
		.catch(error => error);
};

export const getThinkerOrganizations = () => {
	return axios
		.post("aggregate/table", {
			table: "organization",
			query: []
		})
		.then(response => {
			return response.data;
		});
};

export const getOrganization = orgId => {
	return axios
		.post("aggregate/table", {
			table: "organization",
			query: [
				{
					where: {
						organizationId: orgId
					}
				}
			]
		})
		.then(response => response.data);
};

export const getAllMappings = organizationId => {
	return axios
		.get("/tables", {
			params: {
				organizationId
			}
		})
		.then(response => response.data)
		.then(data => {
			let tables = data.filter(table => table.static === false);
			tables = tables.map(table => {
				table.tableAlias += table.tableId !== null ? `(${table.tableId})` : "";
				table.keywords = [...table.dimensions, ...table.metrics];
				delete table.dimensions;
				delete table.metrics;
				return table;
			});
			tables = tables.reduce((result, value) => {
				if (result[value.tableName]) {
					console.log(`Duplicate Table Name: ${value.tableName}`);
				}
				result[value.tableName] = value;
				return result;
			}, {});
			return tables;
		});
};

export const saveQuery = (query, qid) => {
	if (qid) {
		return axios.put(`/api/query/${qid}`, query).then(response => response.data.id);
	} else {
		return axios.post(`/api/query`, query).then(response => response.data.id);
	}
};

export const getQueryState = uiStateId => {
	return axios.get(`/api/queryState/${uiStateId}`).then(response => response.data);
};

export const getOrgQueriesUiState = orgId => {
	return axios
		.get(`/api/queryState`, {
			params: {
				filter: {
					where: {
						organizationId: orgId
					},
					order: "modified DESC"
				}
			}
		})
		.then(response => response.data);
};

export const getOrgQueries = orgId => {
	return axios
		.get(`/api/query`, {
			params: {
				filter: {
					where: {
						organizationId: orgId
					},
					order: "modified DESC"
				}
			}
		})
		.then(response => response.data);
};

export const saveUiState = (uqid, queryState) => {
	return axios.put(`/api/queryState/${uqid}`, queryState).then(response => response.data);
};

export const generateNewQueryState = queryState => {
	return axios.post(`/api/queryState`, queryState).then(response => response.data);
};

export const deleteQueryState = queryStateId => {
	return axios.delete(`/api/queryState/${queryStateId}`).then(response => response.data);
};

export const deleteQuery = queryId => {
	return axios.delete(`/api/query/${queryId}`).then(response => response.data);
};

export const getQuery = queryId => {
	return axios.get(`/api/query/${queryId}`).then(response => response.data);
};

export const getQueryResult = queryId => {
	return axios.get(`/query/${queryId}`).then(response => response.data);
};

export const fetchFilterOption = (indicatorObj, isExternal, organizationId) => {
	if (isExternal) {
		indicatorObj["organizationId"] = parseInt(organizationId, 10) || 0;
		indicatorObj["thirdParty"] = {
			exploreId: indicatorObj.queryId
		};
		delete indicatorObj.queryId;
	}
	return axios.post(`/api/facts/filter`, indicatorObj).then(response => response);
};

export const getProfileKeywords = profileId => {
	const SECTIONS_URL = `${apiUrlConfig.apiConfig.p3fyUrl}/profiles/${profileId}/sections?access_token=${localStorage.$LoopBack$accessTokenId}`;
	return fetch(SECTIONS_URL)
		.then(response => response.json())
		.then(section => {
			const sections = section.map(ele => ele.id);
			const QUESTIONS_URL = `${
				apiUrlConfig.apiConfig.p3fyUrl
			}/questions?filter={"where":{"sectionId":{"inq":${JSON.stringify(
				sections
			)}}},"fields":["keyword","questionText", "questionType","allowMultiple"]}&access_token=${
				localStorage.$LoopBack$accessTokenId
			}`;
			return fetch(QUESTIONS_URL)
				.then(response => response.json())
				.then(questions => {
					return questions;
				});
		});
};

export const getProfiles = orgId => {
	const PROGRAM_URL = `${apiUrlConfig.apiConfig.p3fyUrl}/programOrganizations?filter={"where":{"organizationId":${orgId}}}&access_token=${localStorage.$LoopBack$accessTokenId}`;
	return fetch(PROGRAM_URL)
		.then(response => response.json())
		.then(programId => {
			const programIds = programId.map(ele => ele.programId);
			const PROFILES_URL = `${
				apiUrlConfig.apiConfig.p3fyUrl
			}/profiles?filter={"where":{"programId":{"inq":${JSON.stringify(
				programIds
			)}}},"fields":["name","id"]}&access_token=${localStorage.$LoopBack$accessTokenId}`;
			return fetch(PROFILES_URL)
				.then(response => response.json())
				.then(profiles => {
					return profiles;
				});
		});
};
